import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { AlertController, LoadingController, ToastController } from "@ionic/angular";
import { Clipboard } from "@capacitor/clipboard";
import { PaymentLinkService } from "../../services/payment-link.service";
import {
  SavedRide,
  StatusPagamentoLink,
} from "../../../models/pagamento-link.model";

@Component({
    selector: "app-trip-checkout",
    templateUrl: "./trip-checkout.page.html",
    styleUrls: ["./trip-checkout.page.scss"],
    standalone: false
})
export class TripCheckoutPage implements OnInit {
  savedRides: SavedRide[] = [];
  selectedIds: Set<string> = new Set();
  StatusPagamentoLink = StatusPagamentoLink;
  feePercent = 4.5;

  constructor(
    private paymentLinkService: PaymentLinkService,
    private router: Router,
    private loadCtrl: LoadingController,
    private toastCtrl: ToastController,
    private alertCtrl: AlertController,
  ) {}

  ngOnInit() {
    this.loadFeeConfig();
    this.loadSavedRides();
  }

  loadFeeConfig() {
    this.paymentLinkService.getFeeConfig().subscribe({
      next: (res: any) => {
        if (res?.feePercent != null) this.feePercent = Number(res.feePercent);
        else if (res?.data?.feePercent != null) this.feePercent = Number(res.data.feePercent);
      },
      error: () => { /* keep default */ },
    });
  }

  async loadSavedRides() {
    const loading = await this.loadCtrl.create({ message: "Loading..." });
    await loading.present();
    this.paymentLinkService.getSavedRides().subscribe({
      next: (res: any) => {
        this.savedRides = res.data;
        loading.dismiss();
      },
      error: () => loading.dismiss(),
    });
  }

  get subtotal(): number {
    return this.savedRides
      .filter((r) => this.selectedIds.has(r.id))
      .reduce((sum, r) => sum + (r.valorCorrida || 0), 0);
  }

  get feeAmount(): number {
    return Math.round(this.subtotal * (this.feePercent / 100) * 100) / 100;
  }

  get totalSelected(): number {
    return Math.round((this.subtotal + this.feeAmount) * 100) / 100;
  }

  // Pending rides (payment link generated but unpaid) can also be removed, not
  // just saved ones — see the Trip Checkout test report.
  canDelete(ride: SavedRide): boolean {
    return this.isSalva(ride) || this.isPendente(ride);
  }

  async deleteRide(ride: SavedRide, event?: Event) {
    if (event) event.stopPropagation();
    if (!this.canDelete(ride)) return;

    const alert = await this.alertCtrl.create({
      header: "Delete trip?",
      message: "This trip will be removed permanently.",
      buttons: [
        { text: "Cancel", role: "cancel" },
        {
          text: "Delete",
          role: "destructive",
          handler: async () => {
            const loading = await this.loadCtrl.create({ message: "Deleting..." });
            await loading.present();
            this.paymentLinkService.deleteSavedRide(ride.id).subscribe({
              next: () => {
                this.selectedIds.delete(ride.id);
                this.savedRides = this.savedRides.filter((r) => r.id !== ride.id);
                loading.dismiss();
              },
              error: async () => {
                loading.dismiss();
                const toast = await this.toastCtrl.create({ message: "Failed to delete trip.", duration: 2000, position: "bottom" });
                await toast.present();
              },
            });
          },
        },
      ],
    });
    await alert.present();
  }

  toggleSelect(id: string) {
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
  }

  isSelected(id: string): boolean {
    return this.selectedIds.has(id);
  }

  isSalva(ride: SavedRide): boolean {
    return ride.statusPagamentoLink === StatusPagamentoLink.SALVA;
  }

  isPendente(ride: SavedRide): boolean {
    return ride.statusPagamentoLink === StatusPagamentoLink.PENDENTE;
  }

  shareExistingLink(ride: SavedRide) {
    this.router.navigate(["/call/payment-link-share"], {
      state: { url: ride.stripePaymentLinkUrl, valorTotal: ride.valorTotal ?? ride.valorCorrida },
    });
  }

  async copyExistingLink(ride: SavedRide) {
    await Clipboard.write({ string: ride.stripePaymentLinkUrl });
    const toast = await this.toastCtrl.create({ message: "Link copied!", duration: 2000, position: "bottom" });
    await toast.present();
  }

  async sendPaymentLink() {
    if (this.selectedIds.size === 0) {
      return;
    }
    const loading = await this.loadCtrl.create({
      message: "Generating link...",
    });
    await loading.present();
    this.paymentLinkService
      .generatePaymentLink([...this.selectedIds])
      .subscribe({
        next: (res: any) => {
          const body = res.data;
          loading.dismiss();
          this.router.navigate(["/call/payment-link-share"], {
            state: { url: body.url, valorTotal: body.valorTotal },
          });
        },
        error: () => loading.dismiss(),
      });
  }

  goNewTrip() {
    this.router.navigate(["/call/call-driver"]);
  }
}
